/**
 * @file The one file a group message may carry: what is allowed, and how it is read for the request.
 * messaging-service checks size and type again, by the file's bytes.
 */

export const GROUP_ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/pdf'
]

export const GROUP_ATTACHMENT_ACCEPT = GROUP_ATTACHMENT_TYPES.join(', ')

/** 1 MiB, the same limit as the chat composer. */
export const GROUP_ATTACHMENT_MAX_BYTES = 1024 * 1024

export interface AttachmentFile {
  type: string
  size: number
}

export interface GroupAttachmentPayload {
  content: string
  contentType: string
  filename: string
}

/**
 * Why a picked file cannot be attached, or null when it can.
 * @param file - The picked file
 * @returns A message for the merchant, or null
 */
export function groupAttachmentError(file: AttachmentFile): string | null {
  if (!GROUP_ATTACHMENT_TYPES.includes(file.type)) {
    return 'Attach a JPEG, PNG, GIF or PDF file.'
  }
  if (file.size > GROUP_ATTACHMENT_MAX_BYTES) {
    return 'File must be less than 1mb in size'
  }

  return null
}

/**
 * Reads a file into the `attachment` field the group-messages request takes.
 * @param file - The picked file
 * @returns The base64 content, the declared type and the original name; the server cleans the name
 */
export function readGroupAttachment(
  file: File
): Promise<GroupAttachmentPayload> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result)
      resolve({
        content: dataUrl.slice(dataUrl.indexOf(',') + 1),
        contentType: file.type,
        filename: file.name
      })
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}
